import { Modal, Button, Form, Row, Col, Card, Badge, ProgressBar } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import styled from 'styled-components';
import "../../modal.css";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { STATUS } from "../../enum";
import { 
  Building2, User, MapPin, Phone, Mail, Briefcase, 
  FileText, Users, CheckCircle, ChevronLeft, ChevronRight,
  Save, X, AlertCircle, Activity, HeartHandshake, PhoneCall
} from 'lucide-react';

// Styled Components
const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 900px;
    margin-top: 50px;
    z-index: 1050;
  }
  
  .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    background: white;
  }
  
  &.modal {
    background-color: transparent !important;
  }
  
  .modal-backdrop {
    display: none !important;
  }
`;

const ModalHeader = styled(Modal.Header)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 16px 16px 0 0;
  padding: 1.5rem;
  border: none;
  
  .modal-title {
    font-weight: 600;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .btn-close {
    filter: brightness(0) invert(1);
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 20%;
    right: 20%;
    height: 2px;
    background: #e2e8f0;
    z-index: 0;
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const StepCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : props.completed ? '#10b981' : '#e2e8f0'};
  color: ${props => props.active || props.completed ? 'white' : '#94a3b8'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 0 0 4px rgba(102, 126, 234, 0.1)' : 'none'};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StepLabel = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.active ? '#667eea' : '#64748b'};
  font-weight: ${props => props.active ? '600' : '400'};
`;

const FormSection = styled.div`
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionTitle = styled.h5`
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    color: #667eea;
  }
`;

const FormGroup = styled(Form.Group)`
  margin-bottom: 1.25rem;
  
  label {
    color: #475569;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    .required {
      color: #ef4444;
    }
  }
  
  .form-control, .form-select {
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.625rem 0.875rem;
    font-size: 0.9375rem;
    transition: all 0.2s ease;
    
    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
    
    &.is-invalid {
      border-color: #ef4444;
      
      &:focus {
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
      }
    }
  }
  
  .invalid-feedback {
    font-size: 0.8125rem;
    margin-top: 0.25rem;
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const StyledCheckbox = styled(Form.Check)`
  .form-check-input {
    border: 1.5px solid #cbd5e1;
    
    &:checked {
      background-color: #667eea;
      border-color: #667eea;
    }
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  }
  
  .form-check-label {
    color: #475569;
    font-size: 0.875rem;
    margin-left: 0.25rem;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 2rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #7dd3fc;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  
  svg {
    color: #0284c7;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
  
  p {
    margin: 0;
    color: #0c4a6e;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
  margin-top: 2rem;
`;

const StyledButton = styled(Button)`
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.btn-secondary {
    background: white;
    color: #64748b;
    border: 1.5px solid #e2e8f0;
    
    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ambulanceCompanySchema = Yup.object().shape({
    company_name: Yup.string().required("Company name is required"),
    contact_phone: Yup.string().required("Contact phone is required"),
    contact_email: Yup.string().email("Invalid email").required("Contact email is required"),
});

const classifications = [
  { value: 'gov_hospital', label: "Gov't Hospital" },
  { value: 'private_hospital', label: 'Private Hospital' },
  { value: 'health_center', label: 'Health Center' },
  { value: 'specialty_medical_center', label: 'Specialty Medical Center' },
  { value: 'ambulance_care_provider', label: 'Ambulance Care Provider' },
  { value: 'diagnostic_center', label: 'Diagnostic Center' },
  { value: 'other', label: 'Other' },
];

const subcities = ['Arada','Addis Ketema','Bole','Gulelle','Kirkose','Kalite','Kolfe','Lideta','Lafto','Lemi Kura','Yeka'];

const staffRoleOptions = [
  { value: 'paramedics', label: 'Paramedics' },
  { value: 'emt', label: 'EMT' },
  { value: 'physician', label: 'Physician' },
  { value: 'nurse', label: 'Nurse' },
  { value: 'anesthetists', label: 'Anesthetists' },
  { value: 'health_officers', label: 'Health Officers' },
  { value: 'other', label: 'Other' },
];

const documentOptions = [
  { value: 'licenses', label: 'Licenses' },
  { value: 'car_ownership_certificate', label: 'Car ownership certificate' },
  { value: 'insurance_certificate', label: 'Insurance certificate' },
];

const AmbulanceCompanyForm = ({ mode, showModal, hideModal, initialData = {}, onSuccess }) => {
    const isEditMode = mode === "edit";
    const [approveOnCreate, setApproveOnCreate] = useState(true);
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 4;

    const onSubmit = async (values) => {
        try {
            const endpoint = isEditMode ? `/ambulance-companies/${initialData.company_id}` : "/ambulance-companies";
            const method = isEditMode ? axios.put : axios.post;
            const token = localStorage.getItem('d4uToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // Ensure numeric fields are numbers or null
            const toInt = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
            const payload = {
              ...values,
              branches_count: toInt(values.branches_count),
              bls_count: toInt(values.bls_count),
              als_count: toInt(values.als_count),
              transport_only_count: toInt(values.transport_only_count),
            };

            await method(endpoint, payload, { headers });

            // Optionally approve and send activation email on create
            if (!isEditMode && approveOnCreate && payload.contact_email) {
              try {
                const createId = initialData?.company_id || null; // not available; need to refetch list after
                // Refresh list first to get new ID
                if (onSuccess) await onSuccess();
              } catch (_) {}
            }

            toast.success(`Ambulance Company ${isEditMode ? "updated" : "added"} successfully.`);

            // If approveOnCreate, attempt to find the just-created company by name and approve
            if (!isEditMode && approveOnCreate) {
              try {
                const token2 = localStorage.getItem('d4uToken');
                const headers2 = token2 ? { Authorization: `Bearer ${token2}` } : {};
                // Fetch list and locate by unique company_name + contact_email combination
                const list = await axios.get('/ambulance-companies', { headers: headers2 });
                const match = (list.data || []).find(c => c.company_name === payload.company_name && c.contact_email === payload.contact_email);
                if (match && match.company_id) {
                  await axios.post(`/ambulance-companies/${match.company_id}/approve`, {}, { headers: headers2 });
                  toast.success('Company approved and activation email sent');
                } else {
                  toast.info('Company created. Approve manually to send activation email.');
                }
              } catch (e) {
                toast.error(e?.response?.data?.msg || 'Auto-approve failed');
              }
            }

            if (onSuccess) onSuccess();
            hideModal();
        } catch (error) {
            toast.error(`Failed to ${isEditMode ? "update" : "add"} ambulance company.`);

            if (error.response && error.response.data && (error.response.data.errors || error.response.data.message || error.response.data.msg)) {
                toast.error(error.response.data.errors || error.response.data.message || error.response.data.msg);     
            }
        }
    };

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, submitForm, resetForm, setFieldValue } = useFormik({
        initialValues: {
            company_name: initialData.company_name || "",
            institution_classification: initialData.institution_classification || "",
            subcity: initialData.subcity || "",
            woreda: initialData.woreda || "",
            location_gis: initialData.location_gis || "",
            contact_person: initialData.contact_person || "",
            contact_email: initialData.contact_email || "",
            contact_phone: initialData.contact_phone || "",
            contact_phone_2: initialData.contact_phone_2 || "",
            address: initialData.address || "",
            branches_count: initialData.branches_count ?? "",
            provides_ambulance: !!initialData.provides_ambulance,
            bls_count: initialData.bls_count ?? "",
            als_count: initialData.als_count ?? "",
            transport_only_count: initialData.transport_only_count ?? "",
            provides_prehospital: !!initialData.provides_prehospital,
            staff_roles: Array.isArray(initialData.staff_roles) ? initialData.staff_roles : [],
            has_call_center: !!initialData.has_call_center,
            documents: Array.isArray(initialData.documents) ? initialData.documents : [],
            wants_to_join: initialData.wants_to_join !== undefined ? !!initialData.wants_to_join : true,
            submission_date: initialData.submission_date || new Date().toISOString().slice(0,10),
            agreed_to_terms: initialData.agreed_to_terms || true,
            terms_version: initialData.terms_version || 'v1',
            status: initialData.status ?? 0,
        },
        validationSchema: ambulanceCompanySchema,
        enableReinitialize: true,
        onSubmit,
    });

    useEffect(() => {
        if (showModal && !isEditMode) {
            resetForm();
            setCurrentStep(1);
        }
    }, [showModal, isEditMode, resetForm]);

    const toggleArrayValue = (field, option) => {
      const set = new Set(values[field] || []);
      if (set.has(option)) set.delete(option); else set.add(option);
      setFieldValue(field, Array.from(set));
    };
    
    const nextStep = () => {
      if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
    };
    
    const prevStep = () => {
      if (currentStep > 1) setCurrentStep(currentStep - 1);
    };
    
    const getStepIcon = (step) => {
      switch(step) {
        case 1: return <Building2 size={20} />;
        case 2: return <User size={20} />;
        case 3: return <Activity size={20} />;
        case 4: return <FileText size={20} />;
        default: return step;
      }
    };
    
    const getStepLabel = (step) => {
      switch(step) {
        case 1: return 'Institution';
        case 2: return 'Contact';
        case 3: return 'Services';
        case 4: return 'Documents';
        default: return `Step ${step}`;
      }
    };

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <StyledModal
                show={showModal}
                onHide={hideModal}
                backdrop={false}
                keyboard={true}
                aria-labelledby="contained-modal-title-vcenter"
                style={{ zIndex: 9999 }}
            >
                <ModalHeader closeButton>
                    <Modal.Title>
                        <Building2 size={28} />
                        {isEditMode ? "Edit Ambulance Company" : "Register Ambulance Company"}
                    </Modal.Title>
                </ModalHeader>
                <Modal.Body style={{ padding: '2rem' }}>
                    {/* Step Indicator */}
                    <StepIndicator>
                      {[1, 2, 3, 4].map((step) => (
                        <StepItem key={step}>
                          <StepCircle 
                            active={currentStep === step} 
                            completed={currentStep > step}
                          >
                            {currentStep > step ? <CheckCircle size={20} /> : getStepIcon(step)}
                          </StepCircle>
                          <StepLabel active={currentStep === step}>
                            {getStepLabel(step)}
                          </StepLabel>
                        </StepItem>
                      ))}
                    </StepIndicator>

                    {/* Step 1: Institution Information */}
                    {currentStep === 1 && (
                      <FormSection>
                        <SectionTitle>
                          <Building2 size={20} />
                          Institution Information
                        </SectionTitle>
                        
                        <InfoCard>
                          <AlertCircle size={20} />
                          <p>
                            Please provide accurate information about your institution. This will be used for verification and communication purposes.
                          </p>
                        </InfoCard>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                Company Name <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="company_name"
                                name="company_name"
                                value={values.company_name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={errors.company_name && touched.company_name}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.company_name}
                              </Form.Control.Feedback>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Number of Branches</Form.Label>
                              <Form.Control
                                type="number"
                                min={0}
                                id="branches_count"
                                name="branches_count"
                                value={values.branches_count}
                                onChange={handleChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Institution Classification</Form.Label>
                              <Form.Select
                                id="institution_classification"
                                name="institution_classification"
                                value={values.institution_classification}
                                onChange={handleChange}
                              >
                                <option value="">Select classification...</option>
                                {classifications.map(c => (
                                  <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                              </Form.Select>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Subcity</Form.Label>
                              <Form.Select
                                id="subcity"
                                name="subcity"
                                value={values.subcity}
                                onChange={handleChange}
                              >
                                <option value="">Select subcity...</option>
                                {subcities.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </Form.Select>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Woreda</Form.Label>
                              <Form.Control
                                type="text"
                                id="woreda"
                                name="woreda"
                                value={values.woreda}
                                onChange={handleChange}
                                placeholder="Enter woreda"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <MapPin size={16} />
                                Location (GIS Coordinates)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="location_gis"
                                name="location_gis"
                                value={values.location_gis}
                                onChange={handleChange}
                                placeholder="e.g., 9.0103,38.7612"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </FormSection>
                    )}

                    {/* Step 2: Contact Information */}
                    {currentStep === 2 && (
                      <FormSection>
                        <SectionTitle>
                          <User size={20} />
                          Contact Information
                        </SectionTitle>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <User size={16} />
                                Contact Person
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="contact_person"
                                name="contact_person"
                                value={values.contact_person}
                                onChange={handleChange}
                                placeholder="Full name"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <Mail size={16} />
                                Contact Email <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="email"
                                id="contact_email"
                                name="contact_email"
                                value={values.contact_email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={errors.contact_email && touched.contact_email}
                                placeholder="email@example.com"
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.contact_email}
                              </Form.Control.Feedback>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <Phone size={16} />
                                Contact Phone <span className="required">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="contact_phone"
                                name="contact_phone"
                                value={values.contact_phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={errors.contact_phone && touched.contact_phone}
                                placeholder="+251911000000"
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.contact_phone}
                              </Form.Control.Feedback>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <Phone size={16} />
                                Alternate Phone
                              </Form.Label>
                              <Form.Control
                                type="text"
                                id="contact_phone_2"
                                name="contact_phone_2"
                                value={values.contact_phone_2}
                                onChange={handleChange}
                                placeholder="+251911000001"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <FormGroup>
                          <Form.Label>
                            <MapPin size={16} />
                            Address
                          </Form.Label>
                          <Form.Control
                            type="text"
                            id="address"
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            placeholder="Enter full address"
                          />
                        </FormGroup>
                      </FormSection>
                    )}

                    {/* Step 3: Services & Operations */}
                    {currentStep === 3 && (
                      <FormSection>
                        <SectionTitle>
                          <Activity size={20} />
                          Services & Operations
                        </SectionTitle>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <HeartHandshake size={16} />
                                Do you provide ambulance service?
                              </Form.Label>
                              <RadioGroup>
                                <StyledCheckbox
                                  type="radio"
                                  id="provides_ambulance_yes"
                                  label="Yes"
                                  name="provides_ambulance"
                                  checked={!!values.provides_ambulance}
                                  onChange={() => setFieldValue('provides_ambulance', true)}
                                />
                                <StyledCheckbox
                                  type="radio"
                                  id="provides_ambulance_no"
                                  label="No"
                                  name="provides_ambulance"
                                  checked={!values.provides_ambulance}
                                  onChange={() => {
                                    setFieldValue('provides_ambulance', false);
                                    setFieldValue('bls_count', '');
                                    setFieldValue('als_count', '');
                                    setFieldValue('transport_only_count', '');
                                  }}
                                />
                              </RadioGroup>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <Activity size={16} />
                                Do you provide prehospital care?
                              </Form.Label>
                              <RadioGroup>
                                <StyledCheckbox
                                  type="radio"
                                  id="provides_prehospital_yes"
                                  label="Yes"
                                  name="provides_prehospital"
                                  checked={!!values.provides_prehospital}
                                  onChange={() => setFieldValue('provides_prehospital', true)}
                                />
                                <StyledCheckbox
                                  type="radio"
                                  id="provides_prehospital_no"
                                  label="No"
                                  name="provides_prehospital"
                                  checked={!values.provides_prehospital}
                                  onChange={() => setFieldValue('provides_prehospital', false)}
                                />
                              </RadioGroup>
                            </FormGroup>
                          </Col>
                        </Row>

                        {values.provides_ambulance && (
                          <Card className="mb-3" style={{ border: '1px solid #e2e8f0' }}>
                            <Card.Body>
                              <h6 className="mb-3" style={{ color: '#475569', fontWeight: 600 }}>
                                Ambulance Fleet Details
                              </h6>
                              <Row>
                                <Col md={4}>
                                  <FormGroup>
                                    <Form.Label>Functional BLS Ambulances</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min={0}
                                      id="bls_count"
                                      name="bls_count"
                                      value={values.bls_count}
                                      onChange={handleChange}
                                      placeholder="0"
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup>
                                    <Form.Label>Functional ALS Ambulances</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min={0}
                                      id="als_count"
                                      name="als_count"
                                      value={values.als_count}
                                      onChange={handleChange}
                                      placeholder="0"
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup>
                                    <Form.Label>Transport-only Ambulances</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min={0}
                                      id="transport_only_count"
                                      name="transport_only_count"
                                      value={values.transport_only_count}
                                      onChange={handleChange}
                                      placeholder="0"
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        )}

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <Users size={16} />
                                Staff Working in Ambulances
                              </Form.Label>
                              <CheckboxGrid>
                                {staffRoleOptions.map(opt => (
                                  <StyledCheckbox
                                    key={opt.value}
                                    type="checkbox"
                                    id={`staff_${opt.value}`}
                                    label={opt.label}
                                    checked={values.staff_roles.includes(opt.value)}
                                    onChange={() => toggleArrayValue('staff_roles', opt.value)}
                                  />
                                ))}
                              </CheckboxGrid>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>
                                <PhoneCall size={16} />
                                Do you have a dedicated call center?
                              </Form.Label>
                              <RadioGroup>
                                <StyledCheckbox
                                  type="radio"
                                  id="has_call_center_yes"
                                  label="Yes"
                                  name="has_call_center"
                                  checked={!!values.has_call_center}
                                  onChange={() => setFieldValue('has_call_center', true)}
                                />
                                <StyledCheckbox
                                  type="radio"
                                  id="has_call_center_no"
                                  label="No"
                                  name="has_call_center"
                                  checked={!values.has_call_center}
                                  onChange={() => setFieldValue('has_call_center', false)}
                                />
                              </RadioGroup>
                            </FormGroup>
                          </Col>
                        </Row>
                      </FormSection>
                    )}

                    {/* Step 4: Documents & Agreement */}
                    {currentStep === 4 && (
                      <FormSection>
                        <SectionTitle>
                          <FileText size={20} />
                          Documents & Agreement
                        </SectionTitle>

                        <FormGroup>
                          <Form.Label>
                            <Briefcase size={16} />
                            Valid Documents You Have
                          </Form.Label>
                          <CheckboxGrid>
                            {documentOptions.map(opt => (
                              <StyledCheckbox
                                key={opt.value}
                                type="checkbox"
                                id={`doc_${opt.value}`}
                                label={opt.label}
                                checked={values.documents.includes(opt.value)}
                                onChange={() => toggleArrayValue('documents', opt.value)}
                              />
                            ))}
                          </CheckboxGrid>
                        </FormGroup>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Want to join ለAmbulance Network?</Form.Label>
                              <RadioGroup>
                                <StyledCheckbox
                                  type="radio"
                                  id="wants_to_join_yes"
                                  label="Yes"
                                  name="wants_to_join"
                                  checked={!!values.wants_to_join}
                                  onChange={() => setFieldValue('wants_to_join', true)}
                                />
                                <StyledCheckbox
                                  type="radio"
                                  id="wants_to_join_no"
                                  label="No"
                                  name="wants_to_join"
                                  checked={!values.wants_to_join}
                                  onChange={() => setFieldValue('wants_to_join', false)}
                                />
                              </RadioGroup>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Submission Date</Form.Label>
                              <Form.Control
                                type="date"
                                id="submission_date"
                                name="submission_date"
                                value={values.submission_date}
                                onChange={handleChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Terms Version</Form.Label>
                              <Form.Control
                                type="text"
                                id="terms_version"
                                name="terms_version"
                                value={values.terms_version}
                                onChange={handleChange}
                                placeholder="v1"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Form.Label>Status</Form.Label>
                              <Form.Select
                                id="status"
                                name="status"
                                value={values.status}
                                onChange={handleChange}
                              >
                                <option value={0}>Pending</option>
                                <option value={1}>Active</option>
                                <option value={2}>Rejected</option>
                              </Form.Select>
                            </FormGroup>
                          </Col>
                        </Row>

                        {!isEditMode && (
                          <Card className="mt-3" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '1px solid #7dd3fc' }}>
                            <Card.Body>
                              <StyledCheckbox
                                type="checkbox"
                                id="approve_on_create"
                                label={
                                  <span style={{ fontWeight: 500 }}>
                                    <CheckCircle size={16} style={{ marginRight: 8, color: '#10b981' }} />
                                    Approve immediately and send activation email
                                  </span>
                                }
                                checked={approveOnCreate}
                                onChange={(e) => setApproveOnCreate(e.target.checked)}
                              />
                            </Card.Body>
                          </Card>
                        )}
                      </FormSection>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ padding: '1.5rem' }}>
                    <ButtonGroup>
                        <div>
                            {currentStep > 1 && (
                                <StyledButton
                                    variant="secondary"
                                    onClick={prevStep}
                                    disabled={isSubmitting}
                                >
                                    <ChevronLeft size={18} />
                                    Previous
                                </StyledButton>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <StyledButton variant="secondary" onClick={hideModal} disabled={isSubmitting}>
                                <X size={18} />
                                Cancel
                            </StyledButton>
                            {currentStep < totalSteps ? (
                                <StyledButton
                                    variant="primary"
                                    onClick={nextStep}
                                >
                                    Next
                                    <ChevronRight size={18} />
                                </StyledButton>
                            ) : (
                                <StyledButton
                                    variant="primary"
                                    type="submit"
                                    disabled={isSubmitting}
                                    onClick={submitForm}
                                >
                                    <Save size={18} />
                                    {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Company' : 'Register Company')}
                                </StyledButton>
                            )}
                        </div>
                    </ButtonGroup>
                </Modal.Footer>
            </StyledModal>
        </form>
    );
};

export default AmbulanceCompanyForm;
